import { useSelector } from 'react-redux';
import { Text, Grid, Row, Col, SelectPicker, Modal, Button } from 'rsuite';
import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  selectReportingHistory,
  selectReportingHistoryInProgress,
  selectReportingHistorySuccess,
  selectReportingHistoryErrorMsg,
} from '../../../store/slices/licenseView/licenseViewSelectors';
import dayjs from 'dayjs';
import uniq from 'lodash/uniq';
import orderBy from 'lodash/orderBy';
import {
  C_LAST_12_MONTHS,
  C_PERMIT_STATUS_SUBMITTED,
} from '../../../common/consts';
import { PAYMENT_METHOD_LABEL_ENUM } from '../../../common/enums';
import IconIndicator from '../../user-feedback/IconIndicator';
import UnreportedLicensePeriod from './UnreportedLicensePeriod';
import ReportedLicensePeriod from './ReportedLicensePeriod';
import Notice from '../../user-feedback/Notice';
import { getAddressWithUnitNumber, titleize } from '../../../common/utils';
import ResponsiveModal from '../../ResponsiveModal';
import TaxPayableBreakdownTable from '../../tax/BreakdownTable/TaxPayableBreakdownTable';
import { BREAKPOINT_ENUM } from '../../../common/enums';

const ReportingHistory = ({ applicationNumber }) => {
  const reportingHistory = useSelector(selectReportingHistory);
  const reportingHistoryLoading = useSelector(selectReportingHistoryInProgress);
  const reportingHistorySuccess = useSelector(selectReportingHistorySuccess);
  const reportingHistoryErrorMesage = useSelector(
    selectReportingHistoryErrorMsg
  );

  const [selectedYearFilter, setSelectedYearFilter] =
    useState(C_LAST_12_MONTHS);

  // tax summary modal state
  const [taxSummaryModalOpen, setTaxSummaryModalOpen] = useState(false);
  // all curently expanded period-license combinations
  const [activeKeys, setActiveKeys] = useState([]);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= BREAKPOINT_ENUM.SMALL
  );

  const showReportingHistory = useMemo(() => {
    return reportingHistory?.status !== C_PERMIT_STATUS_SUBMITTED;
  }, [reportingHistory?.status]);

  const convertedData = useMemo(() => {
    const convertedLicenses = [];

    if (reportingHistory) {
      // Sorting by startDate in descending order
      const sortedPeriods = [...reportingHistory?.paymentPeriods].sort(
        (a, b) => {
          return dayjs(b.startDate).diff(dayjs(a.startDate));
        }
      );

      sortedPeriods?.forEach((period) => {
        if (period.payments.length) {
          period.payments.forEach((payment, index) => {
            const convertedPayment = {
              providers: [],
              startDate: period.startDate,
              endDate: period.endDate,
              label: period.label,
              uniqueKey: `reported_${period.startDate}_${period.endDate}_${payment.paymentId}_${index}`,
              paymentID: payment.paymentId,

              daysAvailable: payment.daysAvailable,
              daysOccupied: payment.daysOccupied,
              totalTaxableReceipts: payment.taxableReceipts,
              paymentAmount: payment.fractionalPaymentTotal,
              processingFee: payment.fractionalPaymentProcessingFee,
              paidBy:
                PAYMENT_METHOD_LABEL_ENUM[payment.paymentProvider] ||
                payment.paymentProvider,
              reportedBy: payment.reporter,
              reportedDate: dayjs(payment.paymentDate).format('YYYY-MM-DD'),
              isPending: payment.isPending,
              paymentSuccessful: payment.paymentSuccessful,
            };

            payment.taxableActivities.forEach((activity) => {
              let provider = {
                key: activity.provider || 'All',
                sales: activity.totalRevenue,
                deductions: activity.allowableDeductions,
                taxableReceipts: activity.taxableReceipts,
                taxTypes: activity.taxTypes,
              };

              convertedPayment.providers.push(provider);
            });
            convertedLicenses.push(convertedPayment);
          });
        } else {
          const convertedPayment = {
            providers: [],
            startDate: period.startDate,
            endDate: period.endDate,
            label: period.label,
            uniqueKey: `unreported_${period.startDate}_${period.endDate}`,
          };
          convertedLicenses.push(convertedPayment);
        }
      });
    }

    return convertedLicenses;
  }, [reportingHistory]);

  const filteredData = useMemo(() => {
    if (selectedYearFilter === C_LAST_12_MONTHS) {
      return convertedData?.filter(
        (item) =>
          Math.abs(dayjs(item.startDate).diff(dayjs(new Date()), 'month')) < 12
      );
    } else {
      return convertedData?.filter(
        (item) => dayjs(item.startDate).year() === selectedYearFilter
      );
    }
  }, [convertedData, selectedYearFilter]);

  const totPayable = useMemo(() => {
    return reportingHistory?.totPayable || false;
  }, [reportingHistory]);

  const filterOptions = useMemo(() => {
    const years = orderBy(
      uniq(convertedData.map(({ startDate }) => dayjs(startDate).year())),
      (e) => e,
      ['desc']
    );

    const yearOptions = years.map((year) => ({ name: year, value: year }));

    return [
      { name: 'Last 12 months', value: C_LAST_12_MONTHS },
      ...yearOptions,
    ];
  }, [convertedData]);

  const address = useMemo(() => {
    return getAddressWithUnitNumber(
      reportingHistory?.propertyInfo?.propertyAddress,
      reportingHistory?.propertyInfo?.unitNumber
    );
  }, [reportingHistory]);

  const licenseNumber = useMemo(() => {
    return reportingHistory?.certificateInfo?.licenseNumber;
  }, [reportingHistory]);

  // On small screens when an accordion item is clicked its detail should open in a modal
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= BREAKPOINT_ENUM.SMALL);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // eventKey is the paymentID
  const licenseSelected = useCallback(
    (eventKey) => {
      if (activeKeys.includes(eventKey)) {
        const newActiveKeys = activeKeys.filter((item) => item !== eventKey);
        setActiveKeys(newActiveKeys);
      } else {
        const newActiveKey = [...activeKeys, eventKey];
        const selectedLicense = filteredData?.find(
          (license) => license.uniqueKey === eventKey
        );
        setSelectedLicense(selectedLicense);
        setActiveKeys(newActiveKey);
      }
    },
    [isMobile, filteredData, activeKeys]
  );

  useEffect(() => {
    if (isMobile && selectedLicense) {
      setTaxSummaryModalOpen(true);
    } else if (!isMobile) {
      setTaxSummaryModalOpen(false);
    }
  }, [isMobile, selectedLicense]);

  const clearSelectedLicenseModalClose = useCallback(() => {
    setTaxSummaryModalOpen(false);
    setSelectedLicense(null);
    const newActiveKeys = activeKeys.filter(
      (item) => item !== selectedLicense?.uniqueKey
    );
    setActiveKeys(newActiveKeys);
  }, [selectedLicense]);

  const panelExpanded = useCallback(
    (key) => {
      return activeKeys?.includes(key);
    },
    [activeKeys]
  );

  return (
    <>
      <Grid fluid>
        <Row className="mb-24">
          <Col
            xs={24}
            sm={12}
          >
            <Text
              as="h3"
              className="text-bold header-text-h4"
            >
              Reporting History
            </Text>
          </Col>
          {!reportingHistoryLoading && showReportingHistory && (
            <Col
              xs={24}
              sm={12}
              md={6}
              mdPush={6}
              className="pb-16-sm"
            >
              <SelectPicker
                labelKey="name"
                valueKey="value"
                data={filterOptions}
                searchable={false}
                value={selectedYearFilter}
                onSelect={(value) => {
                  setSelectedYearFilter(value);
                }}
                style={{ width: '200px', float: 'right' }}
                cleanable={false}
                menuStyle={{ width: '199px' }}
                className="sm-100-percent filter-picker"
              />
            </Col>
          )}
        </Row>
        {reportingHistoryLoading && (
          <Row>
            <Col xs={24}>
              <IconIndicator />
            </Col>
          </Row>
        )}
        {!reportingHistoryLoading &&
          reportingHistorySuccess &&
          (filteredData.length ? (
            <Row
              className="content-section"
              style={{ minHeight: '258px' }}
            >
              <Col xs={24}>
                {showReportingHistory &&
                  filteredData.map((licensePeriod, index) => (
                    <div
                      className="reporting-history-item"
                      key={licensePeriod.uniqueKey}
                    >
                      {licensePeriod.providers.length > 0 ? (
                        <ReportedLicensePeriod
                          key={`reported_license_period_${index}`}
                          licensePeriod={licensePeriod}
                          onLicenseSelected={(eventKey) => {
                            licenseSelected(eventKey);
                          }}
                          isMobile={isMobile}
                          licenseNumber={licenseNumber}
                          expanded={panelExpanded(licensePeriod.uniqueKey)}
                        />
                      ) : (
                        <UnreportedLicensePeriod
                          key={`unreported_license_period_${index}`}
                          periodStartDate={licensePeriod.startDate}
                          periodEndDate={licensePeriod.endDate}
                          periodLabel={licensePeriod.label}
                          totPayable={totPayable}
                          applicationNumber={applicationNumber}
                          licenseNumber={licenseNumber}
                          address={address}
                        />
                      )}
                    </div>
                  ))}
              </Col>
            </Row>
          ) : (
            <Row>
              <Col xs={24}>
                <Notice
                  type="info"
                  message="No reported history for the selected period"
                />
              </Col>
            </Row>
          ))}
        {!reportingHistorySuccess &&
          !reportingHistoryLoading &&
          reportingHistoryErrorMesage && (
            <Notice message={reportingHistoryErrorMesage} />
          )}
      </Grid>

      <ResponsiveModal
        isOpen={taxSummaryModalOpen}
        onClose={() => {
          clearSelectedLicenseModalClose();
        }}
        ariaLabel="reporting-history"
      >
        {selectedLicense && (
          <>
            <Modal.Body
              aria-label={`modal-reporting-history`}
              className="ph-12 pv-8"
              style={{ maxHeight: 'unset', marginTop: '10px' }}
            >
              <Grid fluid>
                <Row>
                  <Col xs={24}>
                    <Text className="text-bold mb-16">
                      {selectedLicense?.label}
                    </Text>
                  </Col>
                </Row>
                {reportingHistory?.propertyInfo?.propertyAddress && (
                  <Row>
                    <Col xs={24}>
                      <Text className="text-bold mb-16">
                        {titleize(
                          reportingHistory?.propertyInfo?.propertyAddress
                        )}
                      </Text>
                    </Col>
                  </Row>
                )}
              </Grid>
              <TaxPayableBreakdownTable
                period={selectedLicense}
                license={selectedLicense?.providers}
                periodLabel={selectedLicense?.Label}
                processingFee={selectedLicense.processingFee}
                totalPayment={selectedLicense.paymentAmount}
              />
            </Modal.Body>
            <Modal.Footer style={{ padding: '10px' }}>
              <Button
                onClick={() => {
                  clearSelectedLicenseModalClose();
                }}
                className="btn-primary"
                style={{ width: '100%' }}
              >
                Close
              </Button>
            </Modal.Footer>
          </>
        )}
      </ResponsiveModal>
    </>
  );
};

export default ReportingHistory;
